// ABOUTME: Stamps the cart with this theme's name, the cart attribute _jf_theme=horizon (D102, register 44-R67), so an order placed from this build
// carries the note attribute `_jf_theme: horizon` and the production Flow can tag it theme-horizon; and has the page's cart rows and bag count
// follow a cart write that no cart event announced (D183).
//
// One write per cart through jf-cart's updateCart, a /cart/update.js attributes merge that leaves every other attribute as it is (Intelligems' ig*,
// age_ack, the apps' _attribution and the rest). It writes when the page renders a cart that holds items without the stamp (snippets/scripts.liquid hands
// the render state to this module on its script tag, so an empty or already stamped cart costs no request), and after any add that succeeds, whichever
// path made it: the standard shopify:cart:lines-update add that Horizon's product form, jf-cart and the Shopify.actions runtime dispatch, or any script's
// own /cart/add request, which Resource Timing reports once it has answered. The write always follows the add, never delays it and never touches the
// checkout; the stamp's own request is an update, never an add, so nothing it does can call it again, and a page makes at most MAX_ATTEMPTS writes.
// A standard attributes update replaces the cart's whole set, so one that leaves the stamp out is followed by a new stamp.
// The cart page's checkout form carries the same attribute as a hidden field (snippets/jf-cart-products.liquid) and the cart permalink CTAs carry it in
// their URL (blocks/jf-cart-permalink-button.liquid).
//
// The cart follower (D183, on the design ruled in D200). A script that posts to the Cart API itself (Yotpo's free-product reward, Intelligems' gift)
// dispatches no cart event, so the cart rows and the bag count would keep the old cart until the next page. Resource Timing reports every Cart API
// write that answers, whoever made it. Once cart writes have been quiet for QUIET_MS after one, and no cart event is still on its way (its sender is
// writing the cart and will draw it; an event that has not settled in FLIGHT_MS is given up on), the follower reads the cart and compares it with
// what the page itself draws: the key and quantity of each row of each cart-items-component, and each bag count. When they differ it dispatches one
// shopify:cart:lines-update, settled with the cart it read, from the element the theme's own cart events come from; every cart surface answers it
// as it answers a standard action's. It is an update or a remove and never an add, so the drawer stays shut and nothing stamps.
// A read that has not answered in READ_MS is given up on, so one that never answers cannot stop the follower.
// A page that was hidden and is shown again, or that comes back with Back, is compared once too, at once and out of MAX_SHOWN_READS of its own:
// another tab or another page may have changed the cart, and no request of this page's says so. Only a read that began after the return answers
// for it: one that was out when the page came back may hold the cart as it was before the page left, so the cart is read again when it ends.
// The follower keeps no record of what the page showed. The theme's own writes draw themselves, so the read that follows one finds the page showing
// the cart and dispatches nothing; that read is the cost (D198), one for each run of writes, at most MAX_READS a page view (D199). The follower
// calls nothing in the theme's own cart code and wraps nothing: the apps that wrap fetch and XMLHttpRequest are left as they are.
// Something can land late (D227): a cart event that settles after the follower gave up waiting for it, or an answer with a cart list's rows that
// was asked for before the cart last moved on the page (a cart write answered, a cart event began or settled). What it draws may be older than the
// cart, so the follower reads the cart once more after a quiet second and tells the page if it differs. It does not decide which of two answers is
// the older; it looks again.
//
// The top level stays import-free so the decision logic is unit-testable in plain node (scripts/horizon-migrate/test_theme_stamp.mjs); the browser wiring
// imports @theme/jf-cart when it first writes, and @theme/standard-events before it first reads.

export const STAMP_KEY = '_jf_theme';
export const STAMP_VALUE = 'horizon';
export const MAX_ATTEMPTS = 3;
export const QUIET_MS = 1000;
export const FLIGHT_MS = 5000;
export const READ_MS = 5000;
export const MAX_READS = 20;
export const MAX_SHOWN_READS = 5;

const ADD_PATH = /(^|\/)cart\/add(\.js)?$/;
const WRITE_PATH = /(^|\/)cart\/(add|change|update|clear)(\.js)?$/;
const ANY_PATH = /^\//;
/** The standard cart events whose sender draws a cart on the page. */
const CART_EVENTS = ['shopify:cart:lines-update', 'shopify:cart:discount-update', 'shopify:cart:note-update'];
/** The User Timing mark each of the follower's cart reads leaves: the reads of a page view can be counted from it in any browser. */
const READ_MARK = 'jf-cart-follow:read';

/**
 * @param {Object|null|undefined} cart - Cart API JSON.
 * @returns {boolean} Whether the cart carries the stamp.
 */
export const isStamped = (cart) => Boolean(cart && cart.attributes && cart.attributes[STAMP_KEY] === STAMP_VALUE);

/**
 * @param {string} href
 * @param {string} origin - The storefront origin; a request to any other origin is not this cart's.
 * @param {RegExp} path
 * @returns {boolean} Whether the URL is on this storefront at a path the pattern matches.
 */
const isStorefrontPath = (href, origin, path) => {
  try {
    const url = new URL(href, origin);
    return url.origin === new URL(origin).origin && path.test(url.pathname);
  } catch {
    return false;
  }
};

/**
 * @param {PerformanceResourceTiming|null} entry
 * @param {string} origin
 * @param {RegExp} path
 * @returns {boolean} Whether a Resource Timing entry records a fetch or XHR to such a path that answered and may have succeeded: its status is 2xx,
 *   or not reported at all (a browser without responseStatus is taken at its word that the request finished; 0 is a network failure).
 */
const isAnsweredEntry = (entry, origin, path) => {
  if (!entry || (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest')) return false;
  if (!isStorefrontPath(entry.name, origin, path)) return false;
  const status = entry.responseStatus;
  return status === undefined || (status >= 200 && status < 300);
};

/**
 * @param {string} href
 * @param {string} origin
 * @returns {boolean} Whether the URL is a Cart API add (/cart/add or /cart/add.js, market prefix allowed).
 */
export const isCartAddUrl = (href, origin) => isStorefrontPath(href, origin, ADD_PATH);

/**
 * @param {PerformanceResourceTiming|null} entry
 * @param {string} origin
 * @returns {boolean} Whether a Resource Timing entry records an add that answered and may have succeeded.
 */
export const isAddEntry = (entry, origin) => isAnsweredEntry(entry, origin, ADD_PATH);

/**
 * @param {PerformanceResourceTiming|null} entry
 * @param {string} origin
 * @returns {boolean} Whether a Resource Timing entry records a Cart API write that answered and may have succeeded: /cart/add, /cart/change,
 *   /cart/update or /cart/clear, with or without .js, market prefix allowed.
 */
export const isWriteEntry = (entry, origin) => isAnsweredEntry(entry, origin, WRITE_PATH);

/**
 * @param {PerformanceResourceTiming|null} entry
 * @param {string} origin
 * @param {Array<string|undefined>} sections - The section of each cart list on the page.
 * @returns {boolean} Whether a Resource Timing entry records an answer with a cart list's rows: a fetch or XHR on this storefront that answered
 *   and asked for one of the sections (section_id, or among sections).
 */
export const isRowsEntry = (entry, origin, sections) => {
  if (!isAnsweredEntry(entry, origin, ANY_PATH)) return false;
  const params = new URL(entry.name, origin).searchParams;
  const asked = [params.get('section_id'), ...(params.get('sections') || '').split(',')];
  return sections.some((section) => Boolean(section) && asked.includes(section));
};

/**
 * @param {{cart?: {totalQuantity?: number}|null, userErrors?: Array<Object>, detail?: {didError?: boolean}}|undefined} result - What a standard add's
 *   promise settled with.
 * @returns {boolean} False when the add reports a failure (product-form's didError, a standard action's userErrors) or left the cart empty.
 */
export const addSucceeded = (result) => {
  if (!result) return true;
  if (result.detail && result.detail.didError) return false;
  if (Array.isArray(result.userErrors) && result.userErrors.length > 0) return false;
  return !(result.cart && result.cart.totalQuantity === 0);
};

/**
 * @param {{dataset: DOMStringMap}|null} element - The module's script tag (data-cart-items, data-cart-stamped).
 * @returns {{itemCount: number, stamped: boolean}} The cart as the page rendered it.
 */
export const renderState = (element) => ({
  itemCount: Number(element && element.dataset.cartItems) || 0,
  stamped: Boolean(element && element.dataset.cartStamped === 'true'),
});

/**
 * Creates the stamp controller: no write once the cart carries the stamp, one write in flight at a time, at most maxAttempts writes per page. A write
 * that fails is retried on the next trigger; one that answers without the stamp is never repeated.
 * @param {{write: () => Promise<Object>, stamped?: boolean, maxAttempts?: number}} options - write performs the update and resolves with the cart.
 */
export function createStamper({ write, stamped = false, maxAttempts = MAX_ATTEMPTS }) {
  let done = stamped;
  let pending = null;
  let attempts = 0;
  return {
    get stamped() {
      return done;
    },
    /** The cart lost the stamp: the next request writes it again, within the same cap. */
    forget() {
      done = false;
    },
    /** @returns {Promise<void>|null} The write in flight, if any. */
    request() {
      if (done || pending || attempts >= maxAttempts) return pending;
      attempts += 1;
      pending = Promise.resolve()
        .then(write)
        .then((cart) => {
          done = isStamped(cart);
          if (!done) attempts = maxAttempts;
        })
        .catch(() => {})
        .finally(() => {
          pending = null;
        });
      return pending;
    },
  };
}

/**
 * @param {{counts: Array<string>, rows: Array<Array<{key: string, quantity: number}>>}} page - What the page draws of the cart: the text of each
 *   bag count, and for each cart-items-component the key and quantity of each row.
 * @param {{item_count: number, items: Array<{key: string, quantity: number}>}} cart - Cart API JSON.
 * @returns {boolean} Whether every bag count is the cart's item count and every list of rows is the cart's lines. Rows are compared in no order (the
 *   drawer sorts its rows by title). A bag count that shows no number, as it does from a hundred items, is not compared. Nothing else of the cart
 *   is: its attributes and its note are not drawn, and a price is not read off the page.
 */
export const pageShows = (page, cart) => {
  const lines = cart.items.map((item) => `${item.key} ${item.quantity}`).sort().join();
  return (
    page.counts.every((count) => count === '' || count === String(cart.item_count)) &&
    page.rows.every((rows) => rows.map((row) => `${row.key} ${row.quantity}`).sort().join() === lines)
  );
};

/**
 * @param {{rows: Array<Array<{key: string}>>}} page - What the page draws of the cart.
 * @param {{items: Array<{key: string, quantity: number}>}} cart - Cart API JSON.
 * @returns {Array<{id: string, quantity: number}>} The lines a lines update names to bring the page to the cart: every line of the cart, and at
 *   quantity zero every row the page draws for a line the cart no longer holds. Empty when the cart is empty and the page draws no row.
 */
export const linesToTell = (page, cart) => {
  const held = new Set(cart.items.map((item) => item.key));
  const gone = new Set(page.rows.flat().map((row) => row.key).filter((key) => !held.has(key)));
  return [...cart.items.map((item) => ({ id: item.key, quantity: item.quantity })), ...Array.from(gone, (id) => ({ id, quantity: 0 }))];
};

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>} The promise's own outcome, or a rejection when it has not settled in ms.
 */
const within = (promise, ms) =>
  new Promise((resolve, reject) => {
    const limit = setTimeout(() => reject(new Error(`no answer in ${ms} ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(limit);
        resolve(value);
      },
      (error) => {
        clearTimeout(limit);
        reject(error);
      }
    );
  });

/**
 * Creates the cart follower. Once it has been quiet for quietMs after a cart write, and no cart event is still on its way, it reads the cart, asks
 * the page what it draws, and has the page told the cart when the two differ. It keeps nothing of what the page showed: the
 * page is asked at every read, so a cart the theme has drawn itself is found drawn, and a telling that drew nothing is made again after the next
 * write. One read at a time, at most maxReads reads per page view. A read that a cart write or a cart event overtook is not used, and the cart is
 * read again once it has been quiet; nor is a read during which the page came back into view, which the return's own read follows. Something
 * that lands late (a cart event that settles after it was given up on, rows asked for before the cart last moved or before the page last came
 * back into view) moves the cart too, so the cart is read again once it has been quiet.
 * @param {Object} options
 * @param {() => Promise<Object>} options.read - Resolves with the cart.
 * @param {() => Object} options.page - What the page draws of the cart (see pageShows).
 * @param {(cart: Object, page: Object) => void} options.tell - Tells the page the cart; it must not wait for anything.
 * @param {number} [options.quietMs]
 * @param {number} [options.flightMs] - How long a cart event that has not settled holds a read back.
 * @param {number} [options.readMs] - How long a read may take to answer; one that takes longer is given up on, so it cannot hold the follower.
 * @param {number} [options.maxReads]
 * @param {number} [options.maxShownReads] - The reads a page view may make for being shown again; they spend none of maxReads.
 * @param {() => number} [options.now] - The clock, the one Resource Timing's times are on.
 */
export function createFollower({
  read,
  page,
  tell,
  quietMs = QUIET_MS,
  flightMs = FLIGHT_MS,
  readMs = READ_MS,
  maxReads = MAX_READS,
  maxShownReads = MAX_SHOWN_READS,
  now = () => performance.now(),
}) {
  let timer = null;
  let reading = false;
  let flying = 0;
  let moves = 0;
  let reads = 0;
  let shownReads = 0;
  let away = false;
  let returns = 0;
  let answered = 0;
  let movedAt = -Infinity;
  /* The cart moved at `at`: a read that is out is not used, and rows asked for before `at` may be older than what the page draws. */
  const moved = (at = now()) => {
    moves += 1;
    movedAt = Math.max(movedAt, at);
  };

  const wait = () => {
    clearTimeout(timer);
    timer = setTimeout(afterQuiet, quietMs);
  };
  /*
   * Cart writes have been quiet for quietMs: the read they are owed, unless one is out or the theme is writing. Once the page view's reads are
   * spent none is made for them, and a return that no read has answered for has its own.
   */
  const afterQuiet = () => {
    timer = null;
    if (reading) return;
    if (reads >= maxReads) return afterReturn();
    if (flying > 0) return wait();
    reads += 1;
    look();
  };
  /*
   * The read a return is owed, at once and out of maxShownReads. A return is answered only by a read that began after it. None while a read is
   * out or a cart event is on its way (the end of either comes back here), nor while the quiet second runs towards a read that will be made.
   */
  const afterReturn = () => {
    if (answered === returns || reading || flying > 0 || (timer !== null && reads < maxReads) || shownReads >= maxShownReads) return;
    shownReads += 1;
    look();
  };
  const look = async () => {
    reading = true;
    const at = moves;
    const upTo = returns;
    try {
      const cart = await within(read(), readMs);
      /* Not used once a cart write or event has overtaken it, nor once the page has come back since it began: the cart may have moved
         elsewhere. */
      if (moves === at && returns === upTo) {
        const shown = page();
        if (!pageShows(shown, cart)) tell(cart, shown);
      }
    } catch {
      /* A read that fails or does not answer in readMs, or a telling that fails, leaves the page as it is; the next cart write is followed like any other. */
    }
    reading = false;
    if (moves !== at) return wait();
    /* This read began after the first upTo returns and was not overtaken: it was their comparison, or their one try if it failed. */
    answered = upTo;
    afterReturn();
  };

  return {
    /**
     * A cart write answered: the cart is read once it has been quiet.
     * @param {number} [answeredAt] - When it answered, on the clock of `now`; Resource Timing reports a write some time after it has answered.
     */
    onWrite(answeredAt) {
      moved(answeredAt);
      wait();
    },
    /**
     * An answer with a cart list's rows landed, and the page draws it. Asked for before the cart last moved, it may be older than what the page
     * shows: the cart is read once it has been quiet.
     * @param {number} askedAt - When the rows were asked for, on the clock of `now`.
     */
    onRows(askedAt) {
      /*
       * Rows asked for at the very time of the last move are taken as possibly older too: on a coarse clock (Safari's is a millisecond) an older
       * request, a write's answer and an event's settling can all report one time, and one read settles it. Before any move nothing is older.
       */
      const older = askedAt <= movedAt;
      if (!older) return;
      moved();
      wait();
    },
    /** The page went out of view, or was put away for Back: what it draws may be behind the cart when it comes back. */
    onHidden() {
      away = true;
    },
    /**
     * The page is in view again after it left: another tab or another page may have changed the cart meanwhile, so rows asked for before now may
     * be older than the cart. A browser may say so twice for one return (a restored page is told that it is visible and that it is shown); the
     * second time there has been no leaving since the first.
     */
    onShown() {
      if (!away) return;
      away = false;
      returns += 1;
      movedAt = Math.max(movedAt, now());
      afterReturn();
    },
    /**
     * A cart event began: its sender is writing the cart and draws it when the write has answered. A read that is out is not used, and no read is
     * made until the event has settled, or flightMs have passed, and a quiet second after that.
     * @param {Promise<unknown>|undefined} settles - The event's promise.
     */
    onCartEvent(settles) {
      let landed = false;
      const land = () => {
        if (landed) return;
        landed = true;
        flying -= 1;
        if (timer !== null) wait();
        else afterReturn();
      };
      /*
       * Its sender draws when it settles, so the cart moves on the page then. Settled after it was given up on, it may draw rows older than what
       * the page shows: the cart is read once it has been quiet.
       */
      const settled = () => {
        if (!landed) {
          movedAt = now();
          return land();
        }
        moved();
        wait();
      };
      flying += 1;
      moved();
      Promise.resolve(settles).then(settled, settled);
      setTimeout(land, flightMs);
    },
  };
}

/**
 * @returns {{counts: Array<string>, rows: Array<Array<{key: string, quantity: number}>>}} What the page draws of the cart: the text of each bag
 *   count, and for each cart-items-component (the drawer's, the cart page's) each row's line key and the quantity it was rendered with. A quantity
 *   the shopper has typed and not yet sent is on the input's value, not on its value attribute, and is not read.
 */
const drawnCart = () => ({
  counts: Array.from(document.querySelectorAll('cart-icon [ref="cartBubbleCount"]'), (count) => count.textContent.trim()),
  rows: Array.from(document.querySelectorAll('cart-items-component'), (component) =>
    Array.from(component.querySelectorAll('.cart-items__table-row[data-key]'), (row) => ({
      key: row.dataset.key,
      quantity: Number(row.querySelector('input[name="updates[]"]')?.getAttribute('value')),
    }))
  ),
});

/**
 * Starts the cart follower on the page: it reads the cart with a plain GET, tells the page with a standard lines update, and hears every standard
 * cart event but its own as the theme drawing a cart.
 * @returns {{onWrite: (answeredAt?: number) => void, onRows: (askedAt: number) => void}} The follower, for the Resource Timing watch to report cart
 *   writes and cart lists' rows to.
 */
const startFollower = () => {
  let events = null;
  let told = null;
  const follower = createFollower({
    read: async () => {
      events = events || (await import('@theme/standard-events'));
      performance.mark(READ_MARK);
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },
    page: drawnCart,
    tell: (cart, shown) => {
      const lines = linesToTell(shown, cart);
      if (lines.length === 0) return;
      told = new events.CartLinesUpdateEvent({
        action: lines.every((line) => line.quantity === 0) ? 'remove' : 'update',
        context: 'cart',
        lines,
        // The items spare the sticky bar and the product form a cart read of their own; with no sections each cart-items-component renders its own.
        promise: Promise.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items } }),
      });
      // Never a cart-items-component: it does not answer a lines update sent from itself.
      (document.querySelector('cart-drawer-component') || document.body).dispatchEvent(told);
    },
  });
  for (const type of CART_EVENTS) {
    // In the capture phase, so the follower hears an event settle before any cart list does: rows a list asks for because the event handed it none
    // are then asked for after the settle, and are not taken for an older answer.
    document.addEventListener(
      type,
      (event) => {
        if (event !== told) follower.onCartEvent(event.promise);
      },
      true
    );
  }
  // A tab left open hears nothing of what another tab does to the cart, and a page restored by Back shows the cart as it was drawn. The follower
  // is told of each leaving and each return: a page is away while it is out of view (visibilitychange) and once it is put away for another page
  // (pagehide), and back when it is in view again, which a browser says with visibilitychange, with pageshow, or with both.
  const seen = () => (document.visibilityState === 'visible' ? follower.onShown() : follower.onHidden());
  document.addEventListener('visibilitychange', seen);
  window.addEventListener('pageshow', seen);
  window.addEventListener('pagehide', () => follower.onHidden());
  seen();
  return follower;
};

/**
 * Wires the stamp and the follower to the page: the render state, standard add and attributes events and Resource Timing for any other add (the
 * stamp); Resource Timing for every cart write and every answer with a cart list's rows (the follower).
 */
const start = () => {
  const state = renderState(document.querySelector('script[data-jf-theme-stamp]'));
  const follower = startFollower();
  const stamper = createStamper({
    stamped: state.stamped,
    write: async () => {
      const { updateCart } = await import('@theme/jf-cart');
      return updateCart({ attributes: { [STAMP_KEY]: STAMP_VALUE } });
    },
  });

  if (state.itemCount > 0) stamper.request();

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || !event.promise) return;
    event.promise.then((result) => {
      if (addSucceeded(result)) stamper.request();
    }, () => {});
  });

  document.addEventListener('shopify:cart:attributes-update', (event) => {
    if (!Array.isArray(event.attributes) || !event.promise) return;
    if (event.attributes.some((attribute) => attribute.key === STAMP_KEY && attribute.value === STAMP_VALUE)) return;
    event.promise.then((result) => {
      if (result && Array.isArray(result.userErrors) && result.userErrors.length > 0) return;
      stamper.forget();
      if (result && result.cart && result.cart.totalQuantity > 0) stamper.request();
    }, () => {});
  });

  if (typeof PerformanceObserver === 'function') {
    try {
      new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.some((entry) => isAddEntry(entry, window.location.origin))) stamper.request();
        const answered = Math.max(...entries.filter((entry) => isWriteEntry(entry, window.location.origin)).map((entry) => entry.responseEnd));
        if (answered !== -Infinity) follower.onWrite(Number.isFinite(answered) ? answered : undefined);
        const lists = Array.from(document.querySelectorAll('cart-items-component'), (list) => list.dataset.sectionId);
        for (const entry of entries) if (isRowsEntry(entry, window.location.origin, lists)) follower.onRows(entry.startTime);
      }).observe({ type: 'resource', buffered: true });
    } catch {
      /* A browser without Resource Timing observers still stamps from the render state and the standard add event, and follows no cart write. */
    }
  }
};

if (typeof document !== 'undefined') start();
